import { create } from 'zustand';
import { adminService } from '../services/admin-service';
import { plansService } from '../services/plans-service';
import { subscriptionsService } from '../services/subscriptions-service';
import type {
  AdminStats,
  AdminTenant,
  AdminUser,
  FiltroDeDemo,
  Plan,
  Pagination,
} from '../types/admin.types';

interface AdminState {
  // Stats
  stats: AdminStats | null;
  statsLoading: boolean;

  // Tenants
  tenants: AdminTenant[];
  tenantsPagination: Pagination | null;
  tenantsLoading: boolean;

  // Users
  users: AdminUser[];
  usersPagination: Pagination | null;
  usersLoading: boolean;

  // Plans
  plans: Plan[];
  plansLoading: boolean;

  // Actions
  fetchStats: () => Promise<void>;
  fetchTenants: (params?: {
    search?: string;
    status?: string;
    demo?: FiltroDeDemo;
    page?: number;
  }) => Promise<void>;
  fetchUsers: (params?: {
    search?: string;
    role?: string;
    status?: string;
    tenantId?: string;
    page?: number;
  }) => Promise<void>;
  fetchPlans: () => Promise<void>;
  updateTenant: (id: string, data: Record<string, any>) => Promise<void>;
  updateUser: (id: string, data: Record<string, any>) => Promise<void>;
  createPlan: (data: Partial<Plan>) => Promise<void>;
  updatePlan: (id: string, data: Partial<Plan>) => Promise<void>;
  deletePlan: (id: string) => Promise<void>;
  createSubscription: (data: {
    tenantId: string;
    planId: string;
    endDate: string;
    isTrial?: boolean;
    notes?: string;
  }) => Promise<void>;
  updateSubscription: (id: string, data: Record<string, any>) => Promise<void>;
  cancelSubscription: (id: string, reason?: string) => Promise<void>;
}

export const useAdminStore = create<AdminState>((set, get) => ({
  stats: null,
  statsLoading: false,
  tenants: [],
  tenantsPagination: null,
  tenantsLoading: false,
  users: [],
  usersPagination: null,
  usersLoading: false,
  plans: [],
  plansLoading: false,

  fetchStats: async () => {
    set({ statsLoading: true });
    try {
      const stats = await adminService.getStats();
      set({ stats });
    } finally {
      set({ statsLoading: false });
    }
  },

  fetchTenants: async (params) => {
    set({ tenantsLoading: true });
    try {
      const result = await adminService.getTenants(params);
      set({ tenants: result.data, tenantsPagination: result.pagination });
    } finally {
      set({ tenantsLoading: false });
    }
  },

  fetchUsers: async (params) => {
    set({ usersLoading: true });
    try {
      const result = await adminService.getUsers(params);
      set({ users: result.data, usersPagination: result.pagination });
    } finally {
      set({ usersLoading: false });
    }
  },

  fetchPlans: async () => {
    set({ plansLoading: true });
    try {
      const plans = await plansService.getAll();
      set({ plans });
    } finally {
      set({ plansLoading: false });
    }
  },

  updateTenant: async (id, data) => {
    await adminService.updateTenant(id, data);
    get().fetchTenants();
  },

  updateUser: async (id, data) => {
    await adminService.updateUser(id, data);
    get().fetchUsers();
  },

  createPlan: async (data) => {
    await plansService.create(data);
    get().fetchPlans();
  },

  updatePlan: async (id, data) => {
    await plansService.update(id, data);
    get().fetchPlans();
  },

  deletePlan: async (id) => {
    await plansService.remove(id);
    get().fetchPlans();
  },

  createSubscription: async (data) => {
    await subscriptionsService.create(data);
    get().fetchTenants();
    get().fetchStats();
  },

  updateSubscription: async (id, data) => {
    await subscriptionsService.update(id, data);
    get().fetchTenants();
    get().fetchStats();
  },

  cancelSubscription: async (id, reason) => {
    await subscriptionsService.cancel(id, reason);
    get().fetchTenants();
    get().fetchStats();
  },
}));
